/**
 * Spreadsheet preview model (cdb_webos#1435) — pure, no React.
 *
 * Turns a SheetJS worksheet into the grid the preview draws: values formatted
 * by the cell's number format in the reader's language (Excel shows `6.600 €`
 * in German, `€6,600` in English; SheetJS's own `w` is always en-US), merged
 * cells, column widths, hidden rows/columns/sheets left out, fills from the
 * file. Every place that previews a workbook (chat, Drive, shared links,
 * sources, training, ticket attachments) renders through this one model.
 */
import type { CellObject, WorkBook, WorkSheet } from '@e965/xlsx'

/** Rows/columns drawn at most; a bigger sheet says what it left out. */
export const MAX_ROWS = 2000
export const MAX_COLS = 200
const DEFAULT_COL_PX = 72
const MIN_COL_PX = 36
const MAX_COL_PX = 480

export type CellKind = 'empty' | 'text' | 'number' | 'date' | 'bool' | 'error'

export interface GridCell {
  text: string
  kind: CellKind
  colSpan?: number
  rowSpan?: number
  /** Fill colour from the file (`#rrggbb`), with a text colour that reads on it. */
  fill?: { background: string; color: string }
  /** The cell's formula, shown as tooltip. */
  formula?: string
}

export interface GridRow {
  /** 1-based row number as Excel shows it. */
  number: number
  /** null = covered by a merge from above or the left. */
  cells: (GridCell | null)[]
}

export interface GridSheet {
  name: string
  columns: { letter: string; width: number }[]
  rows: GridRow[]
  /** Rows/columns past the drawing limit (0 when everything is shown). */
  hiddenRows: number
  hiddenCols: number
}

type Xlsx = typeof import('@e965/xlsx')

/** Visible sheets of a workbook as grids. */
export function buildWorkbook(XLSX: Xlsx, workbook: WorkBook, locale: string): GridSheet[] {
  const meta = workbook.Workbook?.Sheets ?? []
  return workbook.SheetNames.filter((_, i) => !meta[i]?.Hidden).map((name) =>
    buildSheet(XLSX, name, workbook.Sheets[name], locale),
  )
}

export function buildSheet(XLSX: Xlsx, name: string, sheet: WorkSheet, locale: string): GridSheet {
  if (!sheet?.['!ref']) return { name, columns: [], rows: [], hiddenRows: 0, hiddenCols: 0 }
  const range = XLSX.utils.decode_range(sheet['!ref'])
  const colInfo = sheet['!cols'] ?? []
  const rowInfo = sheet['!rows'] ?? []

  const lastCol = Math.min(range.e.c, range.s.c + MAX_COLS - 1)
  const visibleCols: number[] = []
  for (let c = range.s.c; c <= lastCol; c++) if (!colInfo[c]?.hidden) visibleCols.push(c)

  const lastRow = Math.min(range.e.r, range.s.r + MAX_ROWS - 1)
  const visibleRows: number[] = []
  for (let r = range.s.r; r <= lastRow; r++) if (!rowInfo[r]?.hidden) visibleRows.push(r)

  const merges = sheet['!merges'] ?? []
  const covered = new Set<string>()
  const spans = new Map<string, { colSpan: number; rowSpan: number }>()
  for (const m of merges) {
    const cols = visibleCols.filter((c) => c >= m.s.c && c <= m.e.c).length
    const rows = visibleRows.filter((r) => r >= m.s.r && r <= m.e.r).length
    spans.set(`${m.s.r}:${m.s.c}`, { colSpan: Math.max(cols, 1), rowSpan: Math.max(rows, 1) })
    for (let r = m.s.r; r <= m.e.r; r++)
      for (let c = m.s.c; c <= m.e.c; c++) if (r !== m.s.r || c !== m.s.c) covered.add(`${r}:${c}`)
  }

  const rows: GridRow[] = visibleRows.map((r) => ({
    number: r + 1,
    cells: visibleCols.map((c) => {
      if (covered.has(`${r}:${c}`)) return null
      const cell = formatCell(XLSX, sheet[XLSX.utils.encode_cell({ r, c })] as CellObject | undefined, locale)
      const span = spans.get(`${r}:${c}`)
      if (span && (span.colSpan > 1 || span.rowSpan > 1)) {
        if (span.colSpan > 1) cell.colSpan = span.colSpan
        if (span.rowSpan > 1) cell.rowSpan = span.rowSpan
      }
      return cell
    }),
  }))

  return {
    name,
    columns: visibleCols.map((c) => ({ letter: XLSX.utils.encode_col(c), width: columnWidth(colInfo[c]) })),
    rows,
    hiddenRows: Math.max(0, range.e.r - lastRow),
    hiddenCols: Math.max(0, range.e.c - lastCol),
  }
}

function columnWidth(info: { wpx?: number; wch?: number; width?: number } | undefined): number {
  const px = info?.wpx ?? (info?.wch != null ? Math.round(info.wch * 7 + 5) : undefined)
  if (px == null) return DEFAULT_COL_PX
  return Math.min(MAX_COL_PX, Math.max(MIN_COL_PX, px))
}

const ERROR_TEXT: Record<number, string> = {
  0x00: '#NULL!', 0x07: '#DIV/0!', 0x0f: '#VALUE!', 0x17: '#REF!', 0x1d: '#NAME?', 0x24: '#NUM!', 0x2a: '#N/A',
}

export function formatCell(XLSX: Xlsx, cell: CellObject | undefined, locale: string): GridCell {
  if (!cell || cell.v == null || cell.t === 'z') return { text: '', kind: 'empty' }
  const out: GridCell = { text: '', kind: 'text' }
  if (cell.f) out.formula = `=${cell.f}`
  const fill = fillOf(cell)
  if (fill) out.fill = fill

  switch (cell.t) {
    case 'e':
      return { ...out, kind: 'error', text: cell.w ?? ERROR_TEXT[cell.v as number] ?? '#ERR' }
    case 'b':
      return { ...out, kind: 'bool', text: booleanText(cell.v as boolean, locale) }
    case 'd':
      return { ...out, kind: 'date', text: formatDate(cell.v as Date, String(cell.z ?? ''), locale) }
    case 'n': {
      const format = String(cell.z ?? 'General')
      if (XLSX.SSF.is_date(format)) {
        const parsed = XLSX.SSF.parse_date_code(cell.v as number)
        const date = new Date(parsed.y, parsed.m - 1, parsed.d, parsed.H, parsed.M, Math.floor(parsed.S))
        return { ...out, kind: 'date', text: formatDate(date, format, locale) }
      }
      const text = formatNumber(cell.v as number, format, locale)
      return { ...out, kind: 'number', text: text ?? cell.w ?? String(cell.v) }
    }
    default:
      return { ...out, kind: 'text', text: String(cell.w ?? cell.v) }
  }
}

function booleanText(value: boolean, locale: string): string {
  if (locale.startsWith('de')) return value ? 'WAHR' : 'FALSCH'
  return value ? 'TRUE' : 'FALSE'
}

/** The part of a number format that applies to ``value`` without its literals. */
function sectionFor(format: string, value: number): { section: string; negativeHasOwn: boolean } {
  const sections = format.split(/;(?=(?:[^"]*"[^"]*")*[^"]*$)/)
  if (value < 0 && sections.length > 1) return { section: sections[1], negativeHasOwn: true }
  if (value === 0 && sections.length > 2) return { section: sections[2], negativeHasOwn: false }
  return { section: sections[0], negativeHasOwn: false }
}

/**
 * ``value`` formatted by ``format`` in ``locale``; null for formats this does
 * not understand (fractions, conditions), where SheetJS's own text is kept.
 */
export function formatNumber(value: number, format: string, locale: string): string | null {
  if (!Number.isFinite(value)) return String(value)
  if (/^general$/i.test(format.trim()) || format === '') {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 10, useGrouping: false }).format(
      Number(value.toPrecision(15)),
    )
  }
  if (format === '@') return String(value)
  const { section: raw, negativeHasOwn } = sectionFor(format, value)
  if (/\?\/|\[[<>=]/.test(raw)) return null

  const currency = currencyOf(raw)
  // Drop colours, locale tags, quoted literals and escapes; what stays decides
  // decimals, grouping, percent and exponent.
  const section = raw
    .replace(/\[[^\]]*\]/g, '')
    .replace(/"[^"]*"/g, '')
    .replace(/\\./g, '')
    .replace(/[_*]./g, '')
  const parenthesised = negativeHasOwn && /\(.*\)/.test(raw)
  const magnitude = negativeHasOwn ? Math.abs(value) : value

  const numberPart = section.match(/[#0?,.]+(?:[eE][+-]?0+)?/)?.[0]
  if (!numberPart) return section.trim() || null
  const [integerPart, fractionPart = ''] = numberPart.split('.')
  const decimals = (fractionPart.match(/[0#?]/g) ?? []).length
  const minDecimals = (fractionPart.match(/0/g) ?? []).length
  const grouping = integerPart.includes(',') && /[0#?],[0#?]/.test(integerPart)
  // Trailing commas scale by 1000 each (`#,##0,` shows thousands).
  const scale = Math.pow(1000, (integerPart.match(/,+$/)?.[0].length ?? 0))

  let text: string
  if (/[eE][+-]/.test(numberPart)) {
    text = magnitude.toExponential(decimals).replace('.', locale.startsWith('de') ? ',' : '.').toUpperCase()
  } else if (section.includes('%')) {
    text = new Intl.NumberFormat(locale, {
      style: 'percent', minimumFractionDigits: minDecimals, maximumFractionDigits: decimals, useGrouping: grouping,
    }).format(magnitude)
  } else if (currency) {
    text = new Intl.NumberFormat(locale, {
      style: 'currency', currency, minimumFractionDigits: minDecimals, maximumFractionDigits: decimals,
      useGrouping: grouping,
    }).format(magnitude / scale)
  } else {
    text = new Intl.NumberFormat(locale, {
      minimumFractionDigits: minDecimals, maximumFractionDigits: decimals, useGrouping: grouping,
    }).format(magnitude / scale)
  }
  return parenthesised ? `(${text})` : text
}

function currencyOf(section: string): string | null {
  if (/€|\[\$€|EUR/.test(section)) return 'EUR'
  if (/\[\$\$|^\$|[^[]\$/.test(section)) return 'USD'
  if (/£/.test(section)) return 'GBP'
  if (/CHF/.test(section)) return 'CHF'
  return null
}

export function formatDate(date: Date, format: string, locale: string): string {
  if (Number.isNaN(date.getTime())) return ''
  const f = format.toLowerCase().replace(/"[^"]*"/g, '').replace(/\[[^\]]*\]/g, '')
  const hasTime = /h|ss|am\/pm/.test(f)
  const hasDate = /d|y/.test(f) || /m{3,}/.test(f) || (!hasTime && /m/.test(f))
  const options: Intl.DateTimeFormatOptions = {}
  if (hasDate || !hasTime) Object.assign(options, { day: '2-digit', month: '2-digit', year: 'numeric' })
  if (hasTime) Object.assign(options, { hour: '2-digit', minute: '2-digit', ...(f.includes('ss') ? { second: '2-digit' } : {}) })
  return new Intl.DateTimeFormat(locale, options).format(date)
}

/** A solid fill from the file, with black or white text, whichever reads better. */
function fillOf(cell: CellObject): GridCell['fill'] | undefined {
  const style = cell.s as { patternType?: string; fgColor?: { rgb?: string } } | undefined
  const rgb = style?.patternType === 'solid' ? style.fgColor?.rgb : undefined
  if (!rgb || !/^[0-9a-f]{6,8}$/i.test(rgb)) return undefined
  const hex = rgb.slice(-6)
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255)
  const lin = (v: number) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4)
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
  return { background: `#${hex}`, color: luminance > 0.4 ? '#111111' : '#ffffff' }
}

/** Language tag for Intl from the UI language (`de`, `en`, `de-DE` …). */
export function sheetLocale(language: string | undefined): string {
  return (language ?? 'de').toLowerCase().startsWith('en') ? 'en-US' : 'de-DE'
}
